/**
 * ParkingManagement Component
 *
 * Manages parking zones and their spots in equipment settings.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Stack,
  Chip,
  IconButton,
  Grid,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {
  useParkingZones,
  useDeleteParkingZone,
  type ParkingZone,
} from '@/hooks/useParking';
import { useNotify } from '@/stores/notificationStore';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { ParkingZoneFormDialog } from './ParkingZoneFormDialog';
import { ParkingSpotsList } from './ParkingSpotsList';
import { useAuthStore } from '@/stores/authStore';

export function ParkingManagement() {
  const { t } = useTranslation('equipment');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  // Zone/spot mutations require equipment.manage server-side; hide what would 403.
  const canManageEquipment = hasPermission('equipment.manage');

  const { data: zones, isLoading, error } = useParkingZones();
  const deleteMutation = useDeleteParkingZone();

  const [formOpen, setFormOpen] = useState(false);
  const [editingZone, setEditingZone] = useState<ParkingZone | null>(null);
  const [deleteZone, setDeleteZone] = useState<ParkingZone | null>(null);
  const [expandedZoneId, setExpandedZoneId] = useState<string | null>(null);

  const handleEdit = (zone: ParkingZone) => {
    setEditingZone(zone);
    setFormOpen(true);
  };

  const handleCreate = () => {
    setEditingZone(null);
    setFormOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteZone) return;
    try {
      await deleteMutation.mutateAsync(deleteZone.id);
      notify.success(t('parking.zoneDeleted'), t('parking.zoneDeletedDesc'));
    } catch {
      notify.error(tc('error'), t('parking.deleteFailed'));
    }
    setDeleteZone(null);
  };

  const toggleExpand = (zoneId: string) => {
    setExpandedZoneId((prev) => (prev === zoneId ? null : zoneId));
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{tc('error')}</Alert>;
  }

  const activeZones = zones?.filter((z) => z.isActive) ?? [];

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h6">{t('parking.title')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('parking.subtitle')}
          </Typography>
        </Box>
        {canManageEquipment && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreate}
          >
            {t('parking.addZone')}
          </Button>
        )}
      </Stack>

      {activeZones.length === 0 ? (
        <Alert severity="info">
          <Typography fontWeight={600}>{t('parking.noZones')}</Typography>
          <Typography variant="body2">{t('parking.noZonesDesc')}</Typography>
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {activeZones.map((zone) => (
            <Grid size={{ xs: 12 }} key={zone.id}>
              <Card variant="outlined">
                <CardContent sx={{ pb: 1 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {zone.name}
                      </Typography>
                      <Stack direction="row" spacing={1} mt={0.5}>
                        <Chip
                          size="small"
                          label={t(`parking.zoneType${zone.zoneType.charAt(0).toUpperCase() + zone.zoneType.slice(1)}`)}
                          variant="outlined"
                        />
                        <Chip
                          size="small"
                          label={t('parking.spotsCount', {
                            available: zone.availableSpots,
                            total: zone.spotCount,
                          })}
                          color={zone.availableSpots > 0 ? 'success' : 'default'}
                        />
                      </Stack>
                    </Box>
                    <Stack direction="row" spacing={0.5}>
                      {canManageEquipment && (
                        <>
                          <Tooltip title={tc('edit')}>
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(zone)}
                              aria-label={tc('edit')}
                              sx={{ minWidth: 44, minHeight: 44 }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={tc('delete')}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setDeleteZone(zone)}
                              aria-label={tc('delete')}
                              sx={{ minWidth: 44, minHeight: 44 }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      <Tooltip
                        title={
                          expandedZoneId === zone.id
                            ? t('parking.collapseZone')
                            : t('parking.expandZone')
                        }
                      >
                        <IconButton
                          size="small"
                          onClick={() => toggleExpand(zone.id)}
                          aria-label={
                            expandedZoneId === zone.id
                              ? t('parking.collapseZone')
                              : t('parking.expandZone')
                          }
                          sx={{ minWidth: 44, minHeight: 44 }}
                        >
                          {expandedZoneId === zone.id ? (
                            <ExpandLessIcon fontSize="small" />
                          ) : (
                            <ExpandMoreIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>
                </CardContent>
                {expandedZoneId === zone.id && (
                  <CardActions sx={{ px: 2, pb: 2, display: 'block' }}>
                    <ParkingSpotsList zoneId={zone.id} />
                  </CardActions>
                )}
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <ParkingZoneFormDialog
        open={formOpen}
        zone={editingZone}
        onClose={() => {
          setFormOpen(false);
          setEditingZone(null);
        }}
      />

      <ConfirmDialog
        open={!!deleteZone}
        title={t('parking.deleteZoneTitle')}
        message={t('parking.deleteZoneConfirm', { name: deleteZone?.name })}
        confirmColor="error"
        onConfirm={() => void handleDelete()}
        onCancel={() => setDeleteZone(null)}
        loading={deleteMutation.isPending}
      />
    </Box>
  );
}
